import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './SignalLoader.module.css';

/* ── Types ── */

export interface TickerBlip {
  symbol: string;
  change?: number; // % change — negative = red blip
  signal?: 'hot' | 'normal'; // hot = red pulse
}

interface Props {
  message?: string;
  size?: 'small' | 'normal';
  fullPage?: boolean;
  tickers?: TickerBlip[];
}

/* ── Blip positions (scattered around the radar) ── */
const BLIP_POSITIONS = [
  { top: 18, left: 72 },
  { top: 28, left: 82 },
  { top: 68, left: 25 },
  { top: 75, left: 68 },
  { top: 22, left: 38 },
  { top: 42, left: 78 },
  { top: 62, left: 18 },
  { top: 78, left: 42 },
  { top: 32, left: 22 },
  { top: 55, left: 75 },
  { top: 15, left: 55 },
  { top: 82, left: 55 },
];

/* ── Active blip state (visible ticker on the radar) ── */
interface ActiveBlip {
  id: number;
  ticker: TickerBlip;
  posIdx: number;
  startTime: number;
}

const SignalLoader: React.FC<Props> = ({
  message = 'Scanning signals...',
  size = 'normal',
  fullPage = false,
  tickers,
}) => {
  const radarClass = size === 'small' ? styles.radarSmall : styles.radar;
  const containerClass = fullPage ? styles.containerFullPage : styles.containerInline;
  const [activeBlips, setActiveBlips] = useState<ActiveBlip[]>([]);
  const [hotPulse, setHotPulse] = useState(false);
  const nextId = useRef(0);
  const usedPositions = useRef(new Set<number>());

  const hasTickers = tickers && tickers.length > 0;

  // Cycle tickers onto the radar
  useEffect(() => {
    if (!hasTickers) return;

    const spawnBlip = () => {
      const pool = tickers!;
      const ticker = pool[Math.floor(Math.random() * pool.length)];

      // Find an unused position
      const available = BLIP_POSITIONS.map((_, i) => i).filter((i) => !usedPositions.current.has(i));
      if (available.length === 0) return;
      const posIdx = available[Math.floor(Math.random() * available.length)];

      const id = nextId.current++;
      usedPositions.current.add(posIdx);

      const blip: ActiveBlip = { id, ticker, posIdx, startTime: Date.now() };
      setActiveBlips((prev) => [...prev, blip]);

      // Hot signal pulse
      if (ticker.signal === 'hot') {
        setHotPulse(true);
        setTimeout(() => setHotPulse(false), 1200);
      }

      // Remove after 2.5s
      setTimeout(() => {
        usedPositions.current.delete(posIdx);
        setActiveBlips((prev) => prev.filter((b) => b.id !== id));
      }, 2500);
    };

    // Spawn first blip quickly, then every 800-1500ms
    const firstTimeout = setTimeout(spawnBlip, 300);
    const interval = setInterval(spawnBlip, 800 + Math.random() * 700);

    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, [hasTickers, tickers]);

  return (
    <div className={containerClass}>
      <div className={radarClass}>
        {/* Concentric circles */}
        <div className={styles.circle1} />
        <div className={styles.circle2} />
        <div className={styles.circle3} />

        {/* Crosshairs */}
        <div className={styles.crosshairH} />
        <div className={styles.crosshairV} />

        {/* Sweep trail (glow cone) */}
        <div className={styles.sweepTrail} />

        {/* Sweep arm */}
        <div className={styles.sweep}>
          <div className={styles.sweepLine} />
        </div>

        {/* Center dot */}
        <div className={styles.centerDot} />

        {/* Ping ripples */}
        <div className={styles.ping} />
        <div className={styles.ping2} />
        <div className={styles.ping3} />

        {/* Hot signal pulse (red ripple) */}
        {hotPulse && (
          <>
            <div className={styles.hotPing} />
            <div className={styles.hotPing2} />
          </>
        )}

        {/* Dynamic ticker blips */}
        {hasTickers ? (
          activeBlips.map((blip) => {
            const pos = BLIP_POSITIONS[blip.posIdx];
            const isHot = blip.ticker.signal === 'hot';
            const isNeg = (blip.ticker.change ?? 0) < 0;
            return (
              <div
                key={blip.id}
                className={`${styles.tickerBlip} ${isHot ? styles.tickerBlipHot : ''}`}
                style={{ top: `${pos.top}%`, left: `${pos.left}%` }}
              >
                <span
                  className={styles.tickerDot}
                  style={
                    isHot
                      ? {
                          background: 'var(--color-negative, #ef4444)',
                          boxShadow: '0 0 10px var(--color-negative, #ef4444)',
                        }
                      : undefined
                  }
                />
                <span className={`${styles.tickerLabel} ${isHot ? styles.tickerLabelHot : ''}`}>
                  {blip.ticker.symbol}
                  {blip.ticker.change != null && (
                    <span className={isNeg ? styles.tickerDown : styles.tickerUp}>
                      {blip.ticker.change > 0 ? '+' : ''}
                      {blip.ticker.change.toFixed(1)}%
                    </span>
                  )}
                </span>
              </div>
            );
          })
        ) : (
          <>
            {/* Static blips (fallback when no tickers provided) */}
            <div className={styles.blip1} />
            <div className={styles.blip2} />
            <div className={styles.blip3} />
            <div className={styles.blip4} />
          </>
        )}
      </div>

      {message && <span className={styles.message}>{message}</span>}
    </div>
  );
};

export default SignalLoader;
